import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { enroll } from '@openstax/experiments';
import cmsFetch from '~/helpers/cms-fetch';
import { useDataFromPromise } from '~/helpers/page-data-utils';
import PutAway from '~/components/put-away/put-away';
import './shared.scss';
export { PutAway };
export function usePutAway() {
    const [closed, setClosed] = useState(false);
    return [closed, () => React.createElement(PutAway, { onClick: () => setClosed(true) })];
}
// eslint-disable-next-line complexity
function getMode(bannerData) {
    var _a, _b;
    if (!bannerData) {
        return null;
    }
    const expireDate = new Date((_a = bannerData.emergency_expires) !== null && _a !== void 0 ? _a : 0);
    const useEmergency = bannerData.emergency_expires && Date.now() < expireDate.getTime();
    if (useEmergency) {
        return 'emergency';
    }
    if (((_b = bannerData.bannerConfigs) === null || _b === void 0 ? void 0 : _b.length) > 0) {
        return 'banner';
    }
    return null;
}
export function useBannerData() {
    var _a;
    const bannerDataPromise = React.useMemo(() => Promise.all([
        cmsFetch('emergency/'),
        cmsFetch('donations/sitebanner/')
    ])
        .then(([ed, bannerConfigs]) => (Object.assign(Object.assign({}, ed), { bannerConfigs }))), []);
    const bannerData = (_a = useDataFromPromise(bannerDataPromise)) !== null && _a !== void 0 ? _a : null;
    const mode = getMode(bannerData);
    return React.useMemo(() => bannerData ? (Object.assign({ mode }, bannerData)) : null, [mode, bannerData]);
}
/* eslint-disable camelcase */
const contextMatchers = {
    all: () => true,
    homepage: (p) => p === '/',
    subjects: (p) => p.startsWith('/subjects'),
    book_details: (p) => p.startsWith('/details/books/'),
    blog: (p) => p.startsWith('/blog')
};
/* eslint-enable camelcase */
function bannerMatchesPath(banner, pathname) {
    var _a, _b;
    if (banner.context_filter === 'url_pattern') {
        const pattern = banner.url_pattern;
        if (!pattern) {
            return false;
        }
        try {
            return new RegExp(`^${pattern}$`).test(pathname);
        }
        catch (_c) {
            return false;
        }
    }
    return (_b = (_a = contextMatchers[banner.context_filter]) === null || _a === void 0 ? void 0 : _a.call(contextMatchers, pathname)) !== null && _b !== void 0 ? _b : false;
}
export function useFilteredBanners(bannerConfigs) {
    const { pathname } = useLocation();
    return React.useMemo(() => {
        if (!(bannerConfigs === null || bannerConfigs === void 0 ? void 0 : bannerConfigs.length)) {
            return [];
        }
        return bannerConfigs.filter((banner) => bannerMatchesPath(banner, pathname));
    }, [bannerConfigs, pathname]);
}
export function useSelectedBanner(filteredBanners) {
    return React.useMemo(() => {
        if (!filteredBanners.length) {
            return null;
        }
        if (filteredBanners.length === 1) {
            return filteredBanners[0];
        }
        const variants = filteredBanners.map((banner) => ({
            name: banner.name,
            banner
        }));
        return enroll({
            name: 'Site Banner Campaign',
            variants
        }).banner;
    }, [filteredBanners]);
}
